<template>
    <div class="srv-qr">
        <header class="srv-head">
            <button class="srv-icone-btn" aria-label="Retour" @click="$emit('retour')">
                <i class="ri-arrow-left-line"></i>
            </button>
            <div class="srv-head-titre">{{ estService ? 'Bon de commande' : 'QR Paiement' }}</div>
        </header>

        <div class="srv-corps">
            <!-- Accusé temps réel : la caisse a déjà reçu la commande par le canal
                 WebSocket. Le QR reste affiché en secours, mais le scan est inutile. -->
            <div v-if="rtConfirme" class="srv-qr-rt">
                <i class="ri-checkbox-circle-fill"></i>
                <span>{{ rtAck && rtAck.statut === 'chargee'
                    ? 'Chargée en caisse - le scan n\'est plus nécessaire.'
                    : 'Reçue par la caisse en temps réel - le scan n\'est plus nécessaire.' }}</span>
            </div>

            <div class="srv-qr-lum">
                <i class="ri-sun-line"></i>
                <span>Luminosité maximale recommandée</span>
            </div>

            <!-- Seule zone claire de l'app : un QR sur fond sombre se lit mal,
                 et le caissier n'a pas à s'y reprendre à deux fois. -->
            <div class="srv-qr-carte">
                <div class="srv-qr-l1">
                    <span class="srv-qr-table">{{ commande.table ? commande.table.name : 'Sans table' }}</span>
                    <span v-if="clientLabel" class="srv-qr-client">{{ clientLabel }}</span>
                    <span class="srv-qr-num srv-num">{{ commande.numero }}</span>
                </div>

                <!-- En mode service, on met en avant CE QUI PART en cuisine
                     (le neuf), pas le total du repas - le caissier lance la
                     préparation de cette fournée. -->
                <div class="srv-qr-total">
                    <span class="srv-qr-total-lbl">{{ estService ? 'À préparer' : 'Total' }}</span>
                    <span v-if="estService" class="srv-qr-total-val srv-num">
                        {{ nbNeuf }} article{{ nbNeuf > 1 ? 's' : '' }}
                    </span>
                    <span v-else class="srv-qr-total-val srv-num">{{ fmt(total) }}</span>
                </div>

                <!-- Grosse commande : plusieurs QR à présenter l'un après
                     l'autre. Le caissier les lit dans l'ordre qu'il veut. -->
                <div v-if="nbParties > 1" class="srv-qr-parties">
                    <span class="srv-qr-parties-titre srv-num">QR {{ partie + 1 }} / {{ nbParties }}</span>
                    <span class="srv-qr-parties-aide">Commande en {{ nbParties }} QR : faites-les tous lire.</span>
                </div>

                <div class="srv-qr-zone">
                    <canvas ref="canvas" class="srv-qr-canvas"></canvas>
                    <p v-if="erreurQr" class="srv-qr-err">{{ erreurQr }}</p>
                </div>

                <div v-if="nbParties > 1" class="srv-qr-nav">
                    <button class="srv-btn srv-btn-sec" :disabled="partie === 0" @click="partie--">
                        <i class="ri-arrow-left-s-line"></i>
                        <span>Précédent</span>
                    </button>
                    <button class="srv-btn" :disabled="partie >= nbParties - 1" @click="partie++">
                        <span>Suivant</span>
                        <i class="ri-arrow-right-s-line"></i>
                    </button>
                </div>

                <p class="srv-qr-cta">
                    {{ estService ? 'Présentez ce code en caisse pour lancer la préparation.'
                                  : 'Présentez ce code au caissier.' }}
                </p>
            </div>

            <div class="srv-qr-etat" :class="'etat-' + (estService ? 'attente' : commande.statut)">
                <i :class="iconeEtat"></i>
                <span>{{ libelleEtat }}</span>
            </div>

            <div class="srv-qr-actions">
                <!-- ── Mode SERVICE : envoi en cuisine ── -->
                <template v-if="estService">
                    <button class="srv-btn" @click="$emit('envoye')">
                        <i class="ri-check-double-line"></i>
                        <span>Commande envoyée</span>
                    </button>
                    <!-- Mock : joue le rôle du scan en caisse pour parcourir
                         l'écran tant qu'il n'y a pas de caisse au bout du fil. -->
                    <button class="srv-btn srv-btn-sec" @click="$emit('envoye')">
                        <i class="ri-qr-scan-2-line"></i>
                        <span>Simuler le scan en cuisine</span>
                    </button>
                </template>

                <!-- ── Mode PAIEMENT : encaissement ── -->
                <template v-else>
                    <!-- Vérification en cours : le caissier a scanné puis tapé
                         « à encaisser », le téléphone interroge le backend. -->
                    <div v-if="enVerification" class="srv-qr-verif" :class="{ epuise: verifEpuise }">
                        <template v-if="!verifEpuise">
                            <i class="ri-loader-4-line srv-tourne"></i>
                            <span>Vérification de l'encaissement…</span>
                        </template>
                        <template v-else>
                            <i class="ri-time-line"></i>
                            <span>Pas encore confirmé. Réessayez si le caissier vient d'encaisser.</span>
                        </template>
                    </div>

                    <button v-if="enVerification && verifEpuise" class="srv-btn" @click="$emit('reverifier')">
                        <i class="ri-refresh-line"></i>
                        <span>Revérifier</span>
                    </button>

                    <!-- Le caissier tape ICI après avoir scanné : lance la
                         vérification automatique de l'encaissement. -->
                    <button v-if="!enVerification && !verrouillee" class="srv-btn" @click="$emit('a-encaisser')">
                        <i class="ri-secure-payment-line"></i>
                        <span>Commande à encaisser</span>
                    </button>

                    <button v-if="!verrouillee" class="srv-btn srv-btn-sec" @click="$emit('encaissee-par-moi')">
                        <i class="ri-cash-line"></i>
                        <span>Marquer encaissée par moi</span>
                    </button>

                    <!-- La table rappelle le serveur : on rouvre la commande
                         plutôt que d'en créer une seconde pour la même table.
                         Masqué si la commande est déjà encaissée (verrouillée). -->
                    <button v-if="!enVerification && !verrouillee" class="srv-btn srv-btn-sec" @click="$emit('rouvrir')">
                        <i class="ri-add-line"></i>
                        <span>Ajouter des articles</span>
                    </button>
                </template>

                <button class="srv-lien" @click="$emit('retour')">
                    <i class="ri-close-line"></i>
                    <span>Retour</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
    /* eslint-disable */
    import QRCode from "qrcode";
    import { fmt } from "../menu/format";
    import { deltaLigne, labelClient, payloadsQr, estVerrouillee } from "./statuts";

    export default {
        name: "srvqrcommande",
        props: {
            commande: { type: Object, required: true },
            // "service" : envoi en cuisine (ne fige pas le paiement).
            // "paiement" : QR d'encaissement (chemin direct conservé).
            mode: { type: String, default: "paiement" },
            // Accusé de réception temps réel (WebSocket) : { id, statut } ou null.
            // Affiché en bannière quand il concerne CETTE commande - le caissier
            // n'a alors pas eu besoin de scanner.
            rtAck: { type: Object, default: null },
        },
        emits: ["envoye", "encaissee-par-moi", "a-encaisser", "reverifier", "rouvrir", "retour"],
        // partie : rang (à partir de 0) du QR affiché quand la commande en compte plusieurs.
        data: () => ({ erreurQr: "", partie: 0 }),
        computed: {
            estService() { return this.mode === "service"; },
            enVerification() { return this.commande.statut === "verification"; },
            // Commande réglée (caisse ou par moi) : on masque toute action d'édition/encaissement.
            verrouillee() { return estVerrouillee(this.commande.statut); },
            verifEpuise() { return !!(this.commande.verif && this.commande.verif.epuise); },
            clientLabel() { return labelClient(this.commande); },
            /* L'accusé reçu concerne-t-il CETTE commande ? */
            rtConfirme() {
                return !!(this.rtAck && String(this.rtAck.id) === String(this.identifiant));
            },
            total() { return this.commande.lignes.reduce((s, l) => s + l.pu * l.qte, 0); },
            // Articles neufs (ajouts seuls) de la fournée à passer.
            nbNeuf() {
                return this.commande.lignes.reduce((s, l) => s + Math.max(0, deltaLigne(l)), 0);
            },
            /**
             * Identifiant stable de la commande, transporté dans chaque QR :
             * concaténation du pid serveur et de la clé de commande. C'est la clé
             * de corrélation qui permet à la caisse de reconnaître une mise à
             * jour de la même commande plutôt qu'une nouvelle.
             */
            identifiant() {
                const pidServeur = this.commande.collaborateur ? this.commande.collaborateur.pid : "";
                return String(pidServeur) + "-" + String(this.commande.id || "");
            },
            libelleEtat() {
                if (this.estService) return "En attente d'envoi en cuisine";
                if (this.commande.statut === "verification") {
                    return this.verifEpuise ? "Encaissement non confirmé" : "Vérification de l'encaissement…";
                }
                if (this.commande.statut === "caisse") return "Commande chargée en caisse";
                if (this.commande.statut === "moi") return "Encaissée par moi";
                return "En attente de scan";
            },
            iconeEtat() {
                if (this.estService || this.commande.statut === "attente") return "ri-loader-4-line srv-tourne";
                if (this.commande.statut === "verification") {
                    return this.verifEpuise ? "ri-time-line" : "ri-loader-4-line srv-tourne";
                }
                return "ri-checkbox-circle-line";
            },
            /**
             * Charge utile du QR (v2). Volontairement compacte : ni libellés ni
             * prix, la caisse les recalcule depuis son propre catalogue - parti
             * pris déjà retenu pour le panier Scan & Go, il évite qu'un QR
             * périmé impose un prix faux.
             *
             * Chaque ligne porte `qte` (voulu) ET `qteEnvoyee` (déjà connu de la
             * cuisine) : c'est le « marqueur » qui laisse la caisse déduire ce
             * qui est neuf, sans dépendre d'un scan précédent réussi.
             *
             *   k  discriminant : "cmd" (commande serveur, ≠ panier supermarché)
             *   m  mode : "svc" (envoi cuisine) | "pay" (paiement)
             *   id identifiant stable (pid serveur + clé commande)
             *   s  pid serveur   t  pid table   cl  libellé client   n  n° attente
             *
             * Les NOMS (serveur, table) ne transitent plus : la caisse les
             * résout depuis ses propres référentiels à partir des pid. Un nom en
             * moins par entité, c'est autant de densité gagnée sur le QR.
             */
            /* Builder PARTAGÉ avec l'émission WebSocket (statuts.js) : une commande
               qui tient dans un QR produit exactement le texte envoyé par le
               socket ; une grosse commande est découpée en plusieurs QR. */
            payloads() { return payloadsQr(this.commande, this.mode); },
            nbParties() { return this.payloads.length; },
            payload() { return this.payloads[Math.min(this.partie, this.nbParties - 1)]; },
        },
        methods: {
            fmt,
            async dessiner() {
                try {
                    this.erreurQr = "";
                    await QRCode.toCanvas(this.$refs.canvas, this.payload, {
                        width: 260,
                        margin: 2,            // zone de silence : sans elle, beaucoup de douchettes échouent
                        errorCorrectionLevel: "M",
                        // Même encre que le code caisse du Scan & Go : vert
                        // très sombre, contraste suffisant pour une douchette.
                        color: { dark: "#0b3d2e", light: "#FFFFFF" },
                    });
                } catch (e) {
                    console.error("[srv] génération QR:", e);
                    this.erreurQr = "Impossible d'afficher le code.";
                }
            },
        },
        mounted() { this.dessiner(); },
        watch: {
            payload: "dessiner",
            // La commande a changé : le découpage aussi, on repart du premier QR.
            payloads(apres, avant) {
                if (!avant || apres.length !== avant.length || apres[0] !== avant[0]) this.partie = 0;
            },
        },
    };
</script>

<style scoped>
    .srv-qr { display: flex; flex-direction: column; min-height: 100vh; }

    .srv-qr-lum {
        display: flex; align-items: center; justify-content: center; gap: 8px;
        margin: 0 auto var(--srv-md); padding: 8px 16px;
        background: var(--srv-surface-high); border-radius: var(--srv-r-full);
        font-size: 13px; font-weight: 700; color: var(--srv-on-variant);
    }
    .srv-qr-lum i { color: var(--srv-primary); font-size: 17px; }

    /* Bannière d'accusé temps réel : vert plein du Scan & Go. */
    .srv-qr-rt {
        display: flex; align-items: center; gap: 10px;
        margin-bottom: var(--srv-md); padding: 12px 14px;
        background: var(--srv-success-soft); color: var(--srv-primary);
        border: 1px solid var(--srv-primary-soft); border-radius: var(--srv-r);
        font-size: 14px; font-weight: 700; line-height: 1.4;
    }
    .srv-qr-rt i { font-size: 20px; flex-shrink: 0; }

    /* La carte du QR reste blanche - c'est une exigence de lecture optique,
       pas un choix esthétique. Sur la charte claire elle ne tranche plus avec
       le fond : on la détache par une bordure et une ombre légère plutôt que
       par le contraste, comme le fait le panier. */
    .srv-qr-carte {
        background: #ffffff; color: var(--srv-on);
        border: 1px solid var(--srv-outline-variant);
        border-radius: var(--srv-r); padding: var(--srv-md);
        box-shadow: 0 6px 16px rgba(15, 110, 86, .12);
    }

    .srv-qr-l1 { display: flex; align-items: center; justify-content: space-between; gap: var(--srv-sm); }
    .srv-qr-table {
        padding: 5px 11px; border-radius: var(--srv-r-input);
        background: var(--srv-primary); color: var(--srv-on-primary);
        font-size: 13px; font-weight: 800; text-transform: uppercase; letter-spacing: .03em;
    }
    .srv-qr-client {
        padding: 4px 10px; border-radius: var(--srv-r-input);
        background: var(--srv-primary-soft); color: var(--srv-primary);
        font-size: 12px; font-weight: 800; white-space: nowrap;
    }
    .srv-qr-num { font-size: 13px; font-weight: 700; color: var(--srv-outline); margin-left: auto; }

    .srv-qr-total { text-align: center; margin: var(--srv-md) 0; }
    .srv-qr-total-lbl {
        display: block; font-size: 12px; font-weight: 700;
        letter-spacing: .08em; text-transform: uppercase; color: var(--srv-on-variant);
    }
    .srv-qr-total-val { font-size: 30px; font-weight: 800; letter-spacing: -0.02em; }

    .srv-qr-zone { display: flex; flex-direction: column; align-items: center; }
    .srv-qr-canvas { width: 260px; height: 260px; max-width: 100%; display: block; }
    .srv-qr-err { margin: var(--srv-sm) 0 0; font-size: 13px; color: var(--srv-error); }

    .srv-qr-parties { display: flex; flex-direction: column; align-items: center; gap: 2px; margin-bottom: var(--srv-sm); }
    .srv-qr-parties-titre {
        padding: 4px 14px; border-radius: var(--srv-r-full);
        background: var(--srv-warn-soft); color: var(--srv-warn-on);
        font-size: 16px; font-weight: 800;
    }
    .srv-qr-parties-aide { font-size: 12px; color: var(--srv-on-variant); }
    .srv-qr-nav { display: grid; grid-template-columns: 1fr 1fr; gap: var(--srv-sm); margin-top: var(--srv-md); }

    .srv-qr-cta { margin: var(--srv-md) 0 0; text-align: center; font-size: 15px; color: var(--srv-on-variant); }

    .srv-qr-etat {
        display: flex; align-items: center; justify-content: center; gap: 10px;
        margin-top: var(--srv-md); padding: 14px;
        background: var(--srv-surface); border: 1px solid var(--srv-outline-variant);
        border-radius: var(--srv-r); font-size: 15px; font-weight: 700;
    }
    .srv-qr-etat i { font-size: 20px; }
    .srv-qr-etat.etat-attente { color: var(--srv-warn-on); background: var(--srv-warn-soft); border-color: var(--srv-warn-soft); }
    .srv-qr-etat.etat-verification { color: var(--srv-info); background: var(--srv-info-soft); border-color: var(--srv-info-soft); }
    .srv-qr-etat.etat-caisse, .srv-qr-etat.etat-moi { color: var(--srv-primary); background: var(--srv-success-soft); border-color: var(--srv-success-soft); }

    /* Bandeau de vérification d'encaissement (mode paiement). */
    .srv-qr-verif {
        display: flex; align-items: center; justify-content: center; gap: 10px;
        padding: 14px; margin-bottom: var(--srv-sm);
        background: var(--srv-info-soft); color: var(--srv-info);
        border-radius: var(--srv-r); font-size: 14px; font-weight: 700; text-align: center;
    }
    .srv-qr-verif i { font-size: 20px; }
    .srv-qr-verif.epuise { background: var(--srv-warn-soft); color: var(--srv-warn-on); }

    .srv-tourne { animation: srv-rot 1s linear infinite; display: inline-block; }
    @keyframes srv-rot { to { transform: rotate(360deg); } }

    .srv-qr-actions { display: flex; flex-direction: column; gap: var(--srv-sm); margin-top: var(--srv-md); }
    .srv-lien {
        display: flex; align-items: center; justify-content: center; gap: 8px;
        min-height: var(--srv-tap); background: none; border: none;
        color: var(--srv-outline); font-family: inherit; font-size: 15px; cursor: pointer;
    }
</style>
